import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Fingerprint, ShieldCheck } from "lucide-react";
import type { PublicKeyCredentialCreationOptionsJSON, PublicKeyCredentialRequestOptionsJSON } from "@simplewebauthn/browser";
import { Sheet } from "./Sheet";
import { Button, cx, TextField } from "./ui";
import { Avatar, PinPad } from "./bits";
import { MfaStep } from "./MfaStep";
import { useToast } from "./Toast";
import { api, ApiError } from "@/lib/api";
import { useSession } from "@/lib/session";
import { cancelled, createKidPasskey, createPasskey, kidPasskeyOptions, passkeyOptions, passkeysSupported, switchBackOptions, switchBackWithPasskey, usePasskeys } from "@/lib/passkeys";
import type { MeDTO, MemberDTO, MfaChallengeDTO } from "@shared/types";

/**
 * Add a passkey on this device: the parent's password first, then one tap for Face ID / fingerprint
 * (a separate tap, because Safari only shows the passkey prompt straight from one).
 */
export function AddPasskeySheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { user } = useSession();
  const [password, setPassword] = useState("");
  const [options, setOptions] = useState<PublicKeyCredentialCreationOptionsJSON | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function close() {
    setPassword("");
    setOptions(null);
    setError(null);
    onClose();
  }

  async function checkPassword(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setOptions(await passkeyOptions(password));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't start adding a passkey");
    } finally {
      setBusy(false);
    }
  }

  async function create() {
    if (!options) return;
    setBusy(true);
    setError(null);
    try {
      qc.setQueryData(["passkeys"], await createPasskey(options));
      toast.show("Passkey added. Signing in and switching back are one tap now.", "success");
      close();
    } catch (err) {
      if (!cancelled(err)) setError(err instanceof ApiError ? err.message : "Couldn't add a passkey on this device");
      // The challenge is used up either way; start again from the password.
      if (err instanceof ApiError) setOptions(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet open={open} onClose={close} title="Add a passkey">
      {options ? (
        <div className="space-y-4">
          <p className="text-sm text-muted">Now confirm with Face ID, your fingerprint or your phone's passcode.</p>
          <Button block size="lg" onClick={create} loading={busy}>
            <Fingerprint className="size-5" /> Create passkey
          </Button>
          {error && <p className="text-center font-bold text-danger">{error}</p>}
        </div>
      ) : (
        <form onSubmit={checkPassword} className="space-y-4">
          <p className="text-sm text-muted">Then you can sign in, or switch back from a kid's view, with Face ID or your fingerprint instead of your password.</p>
          <input type="email" name="username" autoComplete="username" value={user.email ?? ""} readOnly hidden />
          <TextField
            label="Your password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={error}
          />
          <Button type="submit" block size="lg" loading={busy} disabled={!password}>
            Continue
          </Button>
        </form>
      )}
    </Sheet>
  );
}

/** Swap the signed-in person: drop everything cached for the old one, then open the new one's app. */
function useSwitchTo() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return (me: MeDTO) => {
    qc.clear();
    qc.setQueryData(["me"], me);
    navigate(me.user.role === "parent" ? "/parent" : "/kid", { replace: true });
  };
}

function MemberButton({ member, onClick, note }: { member: Pick<MemberDTO, "avatar" | "color" | "name">; onClick: () => void; note?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center gap-2 rounded-3xl bg-card p-4 shadow-card ring-1 ring-line transition active:scale-95"
    >
      <Avatar member={member} size="lg" />
      <span className="text-lg font-extrabold">{member.name}</span>
      {note && <span className="-mt-2 text-xs font-bold text-muted">{note}</span>}
    </button>
  );
}

/**
 * Parent → kid on this device: one tap on the kid, no PIN (the parent is signed in). The parent
 * is signed out, and getting back takes their passkey or password. With `kidId` (from that kid's
 * own page) the sheet is already set to them, so it's one confirm tap.
 */
export function SwitchToKidSheet({ open, onClose, kidId }: { open: boolean; onClose: () => void; kidId?: string }) {
  const session = useSession();
  const preset = kidId ? session.kids.find((k) => k.id === kidId) : undefined;
  const kids = preset ? [preset] : session.kids;
  const switchTo = useSwitchTo();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const supported = passkeysSupported();
  const passkeys = usePasskeys(open && supported);
  const [adding, setAdding] = useState(false);
  const nudge = supported && passkeys.data?.passkeys.length === 0;

  function close() {
    setError(null);
    onClose();
  }

  async function pick(kidId: string) {
    setBusyId(kidId);
    setError(null);
    try {
      switchTo(await api.post<MeDTO>("/auth/switch/kid", { kidId }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not switch");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Sheet open={open} onClose={close} title={preset ? `Hand to ${preset.name}` : "Switch to a kid"}>
      {nudge && kids.length > 0 && (
        <div className="mb-5 flex items-center gap-3 rounded-2xl bg-brand-soft p-3">
          <Fingerprint className="size-6 shrink-0 text-brand-strong" />
          <p className="min-w-0 flex-1 text-sm font-semibold">Switch back with Face ID or your fingerprint instead of your password.</p>
          <Button size="sm" onClick={() => setAdding(true)}>
            Add passkey
          </Button>
          <AddPasskeySheet open={adding} onClose={() => setAdding(false)} />
        </div>
      )}
      {kids.length === 0 ? (
        <p className="text-center text-muted">Add a kid under Family &amp; kids first.</p>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-muted">
            You'll be signed out while they use it. To come back, tap <b className="text-ink">Back to parent</b>, on every screen of their view.
          </p>
          {preset ? (
            <Button block size="lg" onClick={() => void pick(preset.id)} loading={busyId === preset.id}>
              <Avatar member={preset} size="sm" /> Switch to {preset.name}
            </Button>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {kids.map((k) => (
                <MemberButton
                  key={k.id}
                  member={k}
                  onClick={() => !busyId && void pick(k.id)}
                  note={busyId === k.id ? "Switching…" : undefined}
                />
              ))}
            </div>
          )}
          {error && <p className="text-center font-bold text-danger">{error}</p>}
        </div>
      )}
    </Sheet>
  );
}

/** Always on screen in a kid's view, so handing back never means hunting for it. */
export function BackToParent({ variant }: { variant: "bar" | "sidebar" }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={cx(variant === "bar" && "flex justify-end px-2 py-1")}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cx(
          "flex items-center font-bold text-muted transition hover:bg-sunken hover:text-ink",
          variant === "bar" ? "min-h-9 gap-1.5 rounded-full px-3 text-sm" : "w-full gap-3 rounded-2xl px-3 py-2.5",
        )}
      >
        <ShieldCheck className={variant === "bar" ? "size-4" : "size-5"} /> Back to parent
      </button>
      <SwitchToParentSheet open={open} onClose={() => setOpen(false)} />
    </div>
  );
}

/** A kid adds a passkey on this device: their PIN if they have one, then a tap for Face ID or a fingerprint. */
export function AddKidPasskeySheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast();
  const { user } = useSession();
  const [options, setOptions] = useState<PublicKeyCredentialCreationOptionsJSON | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tries, setTries] = useState(0);
  const [busy, setBusy] = useState(false);

  function close() {
    setOptions(null);
    setError(null);
    onClose();
  }

  async function start(pin?: string) {
    setBusy(true);
    setError(null);
    try {
      setOptions(await kidPasskeyOptions(pin));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't start adding a passkey");
      setTries((n) => n + 1);
    } finally {
      setBusy(false);
    }
  }

  // No PIN to check: only a parent can have got this kid in.
  useEffect(() => {
    if (open && !user.hasPin) void start();
  }, [open, user.hasPin]);

  async function create() {
    if (!options) return;
    setBusy(true);
    setError(null);
    try {
      await createKidPasskey(options);
      toast.show("Passkey added!", "success");
      close();
    } catch (err) {
      if (!cancelled(err)) setError(err instanceof ApiError ? err.message : "Couldn't add a passkey on this device");
      if (err instanceof ApiError) setOptions(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet open={open} onClose={close} title="Add a passkey">
      {options ? (
        <div className="space-y-4">
          <p className="text-sm text-muted">Now use your face, your fingerprint or this device's passcode.</p>
          <Button block size="lg" onClick={create} loading={busy}>
            <Fingerprint className="size-5" /> Create passkey
          </Button>
          {error && <p className="text-center font-bold text-danger">{error}</p>}
        </div>
      ) : user.hasPin ? (
        <div className="space-y-4">
          <p className="text-center text-sm text-muted">Enter your PIN. Next time you can sign in without it.</p>
          <PinPad onComplete={(pin) => void start(pin)} disabled={busy} error={!!error} key={tries} />
          {error && <p className="text-center font-bold text-danger">{error}</p>}
        </div>
      ) : (
        error && <p className="text-center font-bold text-danger">{error}</p>
      )}
    </Sheet>
  );
}

/** Kid → parent on this device. Always takes the parent's password (and second step, if on). */
export function SwitchToParentSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { parents } = useSession();
  const switchTo = useSwitchTo();
  const [parentId, setParentId] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [ticket, setTicket] = useState<string | null>(null);
  const [usePassword, setUsePassword] = useState(false);
  const chosen = parents.length === 1 ? parents[0] : parents.find((p) => p.id === parentId);
  // Fetched when the sheet opens so a tap can go straight to Face ID (Safari needs the tap).
  const [options, setOptions] = useState<PublicKeyCredentialRequestOptionsJSON | null>(null);
  const [passkeyBusy, setPasskeyBusy] = useState(false);

  useEffect(() => {
    if (!open || !passkeysSupported()) return;
    let live = true;
    switchBackOptions()
      .then((o) => live && setOptions(o))
      .catch(() => live && setOptions(null));
    return () => {
      live = false;
    };
  }, [open]);

  async function withPasskey() {
    if (!options) return;
    setPasskeyBusy(true);
    setError(null);
    try {
      switchTo(await switchBackWithPasskey(options));
    } catch (err) {
      if (!cancelled(err)) setError(err instanceof ApiError ? err.message : "That passkey didn't work. Use your password instead.");
      // Each try uses up its challenge; get a fresh one for the next tap.
      setOptions(await switchBackOptions().catch(() => null));
    } finally {
      setPasskeyBusy(false);
    }
  }

  function close() {
    // Past the password the kid is already signed out, so leaving here means signing in fresh.
    if (ticket) return window.location.assign("/login");
    setParentId(null);
    setPassword("");
    setError(null);
    setTicket(null);
    setUsePassword(false);
    setOptions(null);
    onClose();
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!chosen) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<MeDTO | MfaChallengeDTO>("/auth/switch/parent", { parentId: chosen.id, password });
      if ("mfaRequired" in res) setTicket(res.ticket);
      else switchTo(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not switch");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet open={open} onClose={close} title={chosen ? `Back to ${chosen.name}` : "Back to a parent"}>
      {ticket ? (
        <div className="space-y-3">
          <p className="text-sm text-muted">Open your authenticator app and enter the code for Meaningful Rewards.</p>
          <MfaStep
            ticket={ticket}
            onDone={switchTo}
            // The kid was signed out when the password was accepted; start over from sign-in.
            onRestart={() => {
              window.location.assign("/login");
            }}
          />
        </div>
      ) : options && !usePassword ? (
        <div className="space-y-3">
          <p className="text-sm text-muted">Parents only: confirm it's you with Face ID, your fingerprint or your phone's passcode.</p>
          <Button block size="lg" onClick={withPasskey} loading={passkeyBusy}>
            <Fingerprint className="size-5" /> Switch back with passkey
          </Button>
          {error && <p className="text-center font-bold text-danger">{error}</p>}
          <Button
            variant="ghost"
            block
            onClick={() => {
              setError(null);
              setUsePassword(true);
            }}
          >
            Use my password instead
          </Button>
        </div>
      ) : !chosen ? (
        <div className="grid grid-cols-2 gap-3">
          {parents.map((p) => (
            <MemberButton key={p.id} member={p} onClick={() => setParentId(p.id)} />
          ))}
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div className="flex items-center gap-3">
            <Avatar member={chosen} size="md" />
            <p className="text-sm text-muted">Parents only: enter your password to leave the kid's view.</p>
          </div>
          {/* Lets password managers fill the right account. */}
          <input type="email" name="username" autoComplete="username" value={chosen.email ?? ""} readOnly hidden />
          <TextField
            label="Password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={error}
          />
          <Button type="submit" block size="lg" loading={busy} disabled={!password}>
            Switch to {chosen.name}
          </Button>
          {parents.length > 1 && (
            <Button type="button" variant="ghost" block onClick={() => setParentId(null)}>
              A different parent
            </Button>
          )}
        </form>
      )}
    </Sheet>
  );
}
